import dayjs from 'dayjs'


import { PeriodMode } from '@/entities/schedule'

import { useLocalization } from '@/shared/composables/localization'
import { getProfileTimezone } from '@/shared/composables/timezone'

import { ScheduleDateTimeInputPayload } from '../types'

import { periodEndForRepeat } from './recurrence-until'
import { getScheduleTimeOffset } from './schedule-time'

export const generateSummary = (periodMode: PeriodMode, payload: Omit<ScheduleDateTimeInputPayload, 'summary'>): string => {
  const { t, tc } = useLocalization()
  const profileTimezone = getProfileTimezone()
  const now = dayjs().tz(profileTimezone)
  const timeFrom = getScheduleTimeOffset(payload.selectedTime.timeFrom)
  const timeTo = getScheduleTimeOffset(payload.selectedTime.timeTo)
  const timeStart = now.startOf('day').add(timeFrom, 'millisecond').format('HH:mm')
  const timeEnd = now.startOf('day').add(timeTo, 'millisecond').format('HH:mm')
  const isEndNextDay = timeTo < timeFrom
  const timeRangeStr = `${timeStart}–${timeEnd}`

  const getDateEnd = () => {
    const { repeat } = payload.repeatOptions
    if (repeat !== 'week' && repeat !== 'month' && repeat !== 'always') {
      return ''
    }

    const dateEnd = periodEndForRepeat(now, repeat)
    return `${t('common.until')} ${dateEnd.format(t('common.dateFormat'))}`
  }

  switch (periodMode) {
  case PeriodMode.EVERY_DAY:
    let str = t('common.daily')
    const days = payload.repeatOptions.repeatDays
    if (days > 1) {
      str = `${tc('common.every', +days)} ${days} ${tc('common.days', +days)}`
    }
    return `${str} ${timeRangeStr} ${getDateEnd()}`
  case PeriodMode.EVERY_WORK_DAY:
    return `${t('chat.workingDays')} ${timeRangeStr} ${getDateEnd()}`
  case PeriodMode.ONCE: {
    const dt = dayjs(payload.startDate).tz(profileTimezone)
    const dateStr = dt.format(t('common.dateFormat'))
    if (isEndNextDay) {
      const dateEndStr = dt.add(1, 'day').format(t('common.dateFormat'))
      return `${dateStr} ${timeStart} – ${dateEndStr} ${timeEnd}`
    }
    return `${dateStr} ${timeRangeStr}`
  }
  case PeriodMode.EVERY_SPECIAL_MONTH_DAY:
    if (!payload.selectedDays.length) {
      return ''
    }

    const monthDayEndStr = payload.endDate
      ? `${t('common.until')} ${dayjs(payload.endDate).tz(profileTimezone).format(t('common.dateFormat'))}`
      : ''

    const [a, ...b] = [...payload.selectedDays.map(d => Number(d)).sort((a, b) => a - b)].reverse()
    if (!b.length) {
      return `${a} ${t('common.daysOfEachMonth')} ${timeRangeStr} ${monthDayEndStr}`
    }

    b.reverse()
    return `${b.join(', ')} ${t('common.and')} ${a} ${t('common.daysOfEachMonth')} ${timeRangeStr} ${monthDayEndStr}`
  case PeriodMode.EVERY_SPECIAL_WEEK_DAY:
    const weekdayLabels = [
      t('common.su'),
      t('common.mo'),
      t('common.tu'),
      t('common.we'),
      t('common.th'),
      t('common.fr'),
      t('common.sa'),
    ]

    const weekdays = payload.selectedWeekdays.map(k => weekdayLabels[+k % 7]).join(' ')
    return `${weekdays} ${timeRangeStr} ${getDateEnd()}`
  default:
    return ''
  }
}
